/* eslint-disable max-lines */
import { Button } from '@strata/react-buttons';
import { NumberInputFormik, TextAreaFormik } from '@strata/react-inputs';
import { DetailLabelValuePair } from '@strata/react-label-value-pairs';
import { SuggestMultiSelectFormik, SuggestSingleSelectFormik } from '@strata/react-selects';
import { validationStrings } from '@strata/strata-ui-strings';
import Decimal from 'decimal.js';
import { Form, Formik } from 'formik';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';

import { useFormResetRef, useInputFocusRef } from 'components/referralForm/FormHelpers';
import { DAW_CODES, DIRECTIONS_MANUAL_ENTRY } from 'services/constants';
import {
    addQtyUnits,
    getEstDispensedQtyUnits,
    PreferredBillingTypes,
    useProductConfigList,
} from 'services/ProductConfigHelpers';
import { nextSection, updateCapturedData } from 'store/referralForm/referralFormSlice';

/** Enum for dose modifiers */
const DoseModifier = {
    WEIGHT: 'Weight',
    BSA: 'BSA',
};

/** Gets formatted html for dose modifier label */
const getDoseModifierLabel = (modifier) => {
    let units;
    if (modifier === DoseModifier.WEIGHT) {
        units = 'kg';
    } else if (modifier === DoseModifier.BSA) {
        units = (
            <>
                m<sup>2</sup>
            </>
        );
    }
    return (
        <>
            {`Patient ${modifier}`}
            {units && <span style={{ textTransform: 'none' }}>{` (${units})`}</span>}
        </>
    );
};

const calcDosage = (value, hasDoseModifier, doseModifier) => {
    if (hasDoseModifier && !doseModifier) {
        return undefined;
    }

    // 1. Multiply value property from direction_option by dose modifier input (if applicable - multiply by 1 if no modifier input is present)
    return value && new Decimal(value)?.times(hasDoseModifier ? doseModifier : 1);
};

const calcEstDispensedQty = ({ productConfig, value, hasDoseModifier, doseModifier, strength }) => {
    const dosage = calcDosage(value, hasDoseModifier, doseModifier);
    if (
        productConfig?.preferredBillingType === PreferredBillingTypes.MEDICAL &&
        productConfig?.hcpcsPackageSize
    ) {
        // 2. Divide the dosage by hcpcs_package_size for the OID
        // 3. Round the output to the next whole number
        // 4. Multiply the rounded value by hcpcs_package_size for the OID
        return dosage
            ?.div(productConfig.hcpcsPackageSize)
            .ceil()
            .times(productConfig.hcpcsPackageSize);
    }
    if (
        productConfig?.preferredBillingType === PreferredBillingTypes.PHARMACY &&
        productConfig?.ndcPackageSize &&
        strength
    ) {
        // 2. Divide dosage by strength (property from directions_options JSON object)
        // 3. Multiply the result of step 2 by the ndc_package_size
        // 4. Round the result of step 3 to the next multiple of the ndc_package_size
        return dosage
            ?.div(strength)
            .times(productConfig.ndcPackageSize)
            .div(productConfig.ndcPackageSize)
            .ceil()
            .times(productConfig.ndcPackageSize);
    }
    return undefined;
};

export const PrescribingInformation = ({ section, isOpen, width }) => {
    const dispatch = useDispatch();
    const { capturedData, activeSection } = useSelector((state) => state.referralForm);

    const { segmentId, productServiceOid } = capturedData;
    const productConfigList = useProductConfigList({ segmentId, productServiceOid });

    const billingRef = useInputFocusRef(isOpen, activeSection, section);
    const formRef = useFormResetRef(isOpen);

    const directionOptions = productConfigList
        ? [
              ...(productConfigList?.[0]?.directionOptions?.map((option) => ({
                  ...option,
                  name: option.option,
              })) ?? []),
              { name: 'Other', option: DIRECTIONS_MANUAL_ENTRY },
          ]
        : [];

    const strengthUomOptions = [
        ...new Set(
            productConfigList?.[0]?.directionOptions?.map((option) => option.strength_uom) ?? [],
        ),
    ];
    const additionalMedOptions = productConfigList?.[0]?.additionalMedOptions ?? [];

    const onSubmit = (values, { setSubmitting }) => {
        const selectedDirectionOption = directionOptions.find(
            (option) => values.directionOption === option.option,
        );
        dispatch(
            updateCapturedData({
                ...values,
                dosage: addQtyUnits(
                    calcDosage(
                        values.quantity,
                        selectedDirectionOption?.dose_modifier,
                        values.doseModifier,
                    ),
                    values.strengthUom,
                ),
                quantity:
                    selectedDirectionOption?.option === DIRECTIONS_MANUAL_ENTRY
                        ? values.quantity
                        : calcEstDispensedQty({
                              productConfig: productConfigList?.find(
                                  (config) => config.preferredBillingType === values.billing,
                              ),
                              value: values.quantity,
                              hasDoseModifier: selectedDirectionOption?.dose_modifier,
                              doseModifier: values.doseModifier,
                              strength: values.strength,
                          }).toNumber(),
            }),
        );
        dispatch(nextSection(section));
        setSubmitting(false);
    };

    return (
        <Formik
            initialValues={{
                ...capturedData,
                dawCode: capturedData.dawCode ?? DAW_CODES[0].secondaryData,
            }}
            enableReinitialize
            validationSchema={Yup.object({
                billing: Yup.string().required(validationStrings.requiredField.errorMessage),
                directionOption: Yup.string().required(
                    validationStrings.requiredField.errorMessage,
                ),
                strength: Yup.number()
                    .min(0, 'Strength cannot be less than 0')
                    .required(validationStrings.requiredField.errorMessage),
                strengthUom: Yup.string().required(validationStrings.requiredField.errorMessage),
                quantity: Yup.number()
                    .min(0, 'Quantity cannot be less than 0')
                    .required(validationStrings.requiredField.errorMessage),
                manualDirectionText: Yup.string().when('directionOption', {
                    is: DIRECTIONS_MANUAL_ENTRY,
                    then: (schema) => schema.required(validationStrings.requiredField.errorMessage),
                }),
                doseModifier: Yup.number().when('directionOption', {
                    is: (value) =>
                        directionOptions.find((option) => value === option.option)?.dose_modifier,
                    then: (schema) =>
                        schema
                            .min(0, 'Dose modifier cannot be less than 0')
                            .required(validationStrings.requiredField.errorMessage),
                }),
                refills: Yup.number()
                    .min(0, 'Refills cannot be less than 0')
                    .max(20, 'Refills cannot be greater than 20')
                    .required(validationStrings.requiredField.errorMessage),
                dawCode: Yup.string().required(validationStrings.requiredField.errorMessage),
            })}
            onSubmit={onSubmit}
            innerRef={formRef}
        >
            {(formik) => {
                const productConfig = productConfigList?.find(
                    (config) => config.preferredBillingType === formik.values.billing,
                );
                const selectedDirectionOption = directionOptions.find(
                    (option) => formik.values.directionOption === option.option,
                );
                const isManualEntry = selectedDirectionOption?.option === DIRECTIONS_MANUAL_ENTRY;

                return (
                    <Form autoComplete="off" style={{ width }}>
                        <SuggestSingleSelectFormik
                            label="Billing"
                            id="billing"
                            required
                            inputRef={billingRef}
                            isLoading={!productConfigList}
                            values={productConfigList?.map((config) => config.preferredBillingType)}
                        />
                        <SuggestSingleSelectFormik
                            label="Directions"
                            id="directionOption"
                            required
                            isLoading={!productConfigList}
                            values={directionOptions}
                            onChange={(newValue) =>
                                formik.setValues({
                                    ...formik.values,
                                    directionOption: newValue?.option,
                                    strength: newValue?.strength,
                                    strengthUom: newValue?.strength_uom,
                                    quantity: newValue?.value,
                                    manualDirectionText: undefined,
                                    doseModifier:
                                        selectedDirectionOption?.dose_modifier &&
                                        newValue?.dose_modifier
                                            ? formik.values.doseModifier
                                            : undefined,
                                })
                            }
                            selectedItem={selectedDirectionOption}
                        />
                        {selectedDirectionOption?.dose_modifier && (
                            <>
                                <NumberInputFormik
                                    id="doseModifier"
                                    label={getDoseModifierLabel(
                                        selectedDirectionOption?.dose_modifier,
                                    )}
                                    required
                                />
                                <div className="rta_prescribing-information__quantity margin__bottom--medium">
                                    <DetailLabelValuePair
                                        label="Estimated Dispensed Quantity"
                                        value={addQtyUnits(
                                            calcEstDispensedQty({
                                                productConfig,
                                                value: formik.values.quantity,
                                                hasDoseModifier:
                                                    selectedDirectionOption?.dose_modifier,
                                                doseModifier: formik.values.doseModifier,
                                                strength: formik.values.strength,
                                            }),
                                            getEstDispensedQtyUnits(productConfig),
                                        )}
                                    />
                                    <DetailLabelValuePair
                                        label="Dose"
                                        value={addQtyUnits(
                                            calcDosage(
                                                formik.values.quantity,
                                                selectedDirectionOption?.dose_modifier,
                                                formik.values.doseModifier,
                                            ),
                                            formik.values.strengthUom,
                                        )}
                                    />
                                </div>
                            </>
                        )}
                        {isManualEntry && (
                            <>
                                <TextAreaFormik
                                    label="Directions"
                                    id="manualDirectionText"
                                    required
                                />
                                <div className="rta_prescribing-information__strength--manual-entry">
                                    <NumberInputFormik label="Strength" id="strength" required />
                                    <SuggestSingleSelectFormik
                                        id="strengthUom"
                                        name="strengthUom"
                                        required
                                        values={strengthUomOptions}
                                    />
                                </div>
                                <NumberInputFormik
                                    label={
                                        <>
                                            Dispensed Quantity
                                            <span style={{ textTransform: 'none' }}>
                                                {` (${getEstDispensedQtyUnits(productConfig)})`}
                                            </span>
                                        </>
                                    }
                                    id="quantity"
                                    required
                                />
                            </>
                        )}
                        <NumberInputFormik label="Refills" id="refills" required />
                        <SuggestSingleSelectFormik
                            label="Dispense As Written"
                            id="dawCode"
                            required
                            values={DAW_CODES}
                            onChange={(newValue) =>
                                formik.setFieldValue('dawCode', newValue?.secondaryData)
                            }
                            selectedItem={DAW_CODES.find(
                                (code) => formik.values.dawCode === code.secondaryData,
                            )}
                        />
                        {additionalMedOptions.length > 0 && (
                            <SuggestMultiSelectFormik
                                label="Additional Medications"
                                id="additionalMedications"
                                values={additionalMedOptions}
                            />
                        )}
                        <TextAreaFormik label="Special Instructions" id="specialInstructions" />
                        <Button
                            label="Continue"
                            intent={Button.Intent.PRIMARY}
                            type="submit"
                            disabled={!(formik.isValid && formik.dirty)}
                        />
                    </Form>
                );
            }}
        </Formik>
    );
};

PrescribingInformation.propTypes = {
    section: PropTypes.number.isRequired,
    isOpen: PropTypes.bool.isRequired,
    width: PropTypes.string.isRequired,
};
